import apiClient from './apiClient';
//import type { ApiError } from './itemService';

// Define types for the API responses
export interface ItemExchangeRate {
  id: string;
  itemFromId: string;
  itemToId: string;
  quantityFrom: number;
  quantityTo: number;
  gameId: string;
  leagueId: string;
  recordedAt: string;
  itemFrom?: {
    id: string;
    item_name: string;
    item_type?: string;
  };
  itemTo?: {
    id: string;
    item_name: string;
    item_type?: string;
  };
  exchangeRate?: number; // Derived field from backend
}

// GetLatestRatesResponse type
export interface GetLatestRatesResponse {
  exchangeRates: ItemExchangeRate[];
}

/**
 * Fetches latest exchange rates for all items in a game and league
 * @param gameId - The game identifier
 * @param leagueId - The league identifier
 * @returns Promise resolving to an array of ItemExchangeRate objects
 */
export const getLatestExchangeRates = async (gameId: string, leagueId: string): Promise<ItemExchangeRate[]> => {
  try {
    const response = await apiClient.get<ItemExchangeRate[]>(`/item-exchange-rates/latest`, {
      params: {
        gameId,
        leagueId,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching latest exchange rates:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches historical exchange rates for a specific item pair in a game and league
 * @param itemFromId - Source item ID
 * @param itemToId - Target item ID (currency)
 * @param gameId - The game identifier
 * @param leagueId - The league identifier
 * @param startDate - Start date for historical data (ISO format)
 * @param endDate - Optional end date for historical data (ISO format)
 * @returns Promise resolving to an array of ItemExchangeRate objects
 */
export const getHistoricalExchangeRates = async (
  itemFromId: string,
  itemToId: string,
  gameId: string,
  leagueId: string,
  startDate: string,
  endDate?: string,
): Promise<ItemExchangeRate[]> => {
  try {
    const response = await apiClient.post<ItemExchangeRate[]>('/item-exchange-rates/historical', {
      itemFromId,
      itemToId,
      gameId,
      leagueId,
      startDate,
      endDate,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching historical exchange rates:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Utility function to find Exalted, Chaos, and Divine Orb item IDs
 * @param exchangeRates - Array of exchange rates to search for currency items
 * @returns Object containing the IDs for Exalted Orb, Chaos Orb, and Divine Orb if found
 */
export const findCurrencyItemIds = (
  exchangeRates: ItemExchangeRate[],
): {
  exaltedOrbId?: string;
  chaosOrbId?: string;
  divineOrbId?: string;
} => {
  const result: {
    exaltedOrbId?: string;
    chaosOrbId?: string;
    divineOrbId?: string;
  } = {};

  // Look through both itemFrom and itemTo for currency items
  for (const rate of exchangeRates) {
    // Check itemFrom
    if (rate.itemFrom) {
      if (rate.itemFrom.item_name.toLowerCase().includes('exalted orb')) {
        result.exaltedOrbId = rate.itemFrom.id;
      } else if (rate.itemFrom.item_name.toLowerCase().includes('chaos orb')) {
        result.chaosOrbId = rate.itemFrom.id;
      } else if (rate.itemFrom.item_name.toLowerCase().includes('divine orb')) {
        result.divineOrbId = rate.itemFrom.id;
      }
    }

    // Check itemTo
    if (rate.itemTo) {
      if (rate.itemTo.item_name.toLowerCase().includes('exalted orb')) {
        result.exaltedOrbId = rate.itemTo.id;
      } else if (rate.itemTo.item_name.toLowerCase().includes('chaos orb')) {
        result.chaosOrbId = rate.itemTo.id;
      } else if (rate.itemTo.item_name.toLowerCase().includes('divine orb')) {
        result.divineOrbId = rate.itemTo.id;
      }
    }

    // Break early if we found all three
    if (result.exaltedOrbId && result.chaosOrbId && result.divineOrbId) {
      break;
    }
  }

  return result;
};

/**
 * Calculate a moving average for a series of data points
 * @param data - Array of data points
 * @param period - Moving average period (number of points to include)
 * @returns Array of moving average values with same length as input data
 */
export const calculateMovingAverage = (
  data: { timestamp: string; value: number }[],
  period: number,
): { timestamp: string; value: number | null }[] => {
  return data.map((point, index) => {
    if (index < period - 1) {
      // Not enough previous data for a complete window
      return { timestamp: point.timestamp, value: null };
    }

    // Calculate average of current and previous (period-1) points
    const sum = data.slice(index - (period - 1), index + 1).reduce((acc, curr) => acc + curr.value, 0);

    return {
      timestamp: point.timestamp,
      value: sum / period,
    };
  });
};

/**
 * Simple implementation of calculating support and resistance levels
 * @param data - Array of price data points
 * @param sensitivity - How many points needed to identify a level
 * @returns Object containing support and resistance levels
 */
export const calculateTrendLines = (
  data: { timestamp: string; value: number }[],
  sensitivity: number = 2,
): {
  support: number[];
  resistance: number[];
} => {
  const levels: { support: number[]; resistance: number[] } = {
    support: [],
    resistance: [],
  };

  // Simplified algorithm to find potential support/resistance levels
  // Look for local minima and maxima
  for (let i = sensitivity; i < data.length - sensitivity; i++) {
    const current = data[i].value;

    // Check for local minimum (potential support)
    let isMin = true;
    for (let j = i - sensitivity; j <= i + sensitivity; j++) {
      if (j === i) continue;
      if (data[j].value < current) {
        isMin = false;
        break;
      }
    }

    if (isMin) {
      levels.support.push(current);
    }

    // Check for local maximum (potential resistance)
    let isMax = true;
    for (let j = i - sensitivity; j <= i + sensitivity; j++) {
      if (j === i) continue;
      if (data[j].value > current) {
        isMax = false;
        break;
      }
    }

    if (isMax) {
      levels.resistance.push(current);
    }
  }

  // Remove duplicates and sort
  levels.support = [...new Set(levels.support)].sort((a, b) => a - b);
  levels.resistance = [...new Set(levels.resistance)].sort((a, b) => a - b);

  return levels;
};

/**
 * Resets (deletes) all market analysis data for a specific game and league
 * Admin only operation
 * @param gameId - The game identifier
 * @param leagueId - The league identifier
 * @returns Promise resolving to the count of deleted records
 */
export const resetMarketData = async (gameId: string, leagueId: string): Promise<{ deletedCount: number }> => {
  try {
    const response = await apiClient.delete<{ deletedCount: number }>(`/item-exchange-rates/reset-by-game-league`, {
      params: {
        gameId,
        leagueId,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error resetting market data:', error.response?.data || error.message);
    throw error;
  }
};
